import { supabase } from "../supabase/SupabaseClient";

/* Los Excel que llegaron al bot para Mis planillas (FiboSheets #10).
 *
 * El bot (FlotaBot, `fibosheets_importar.py`) sube el archivo al bucket
 * privado `importaciones`, en la carpeta del dueño, y anota una fila
 * `pendiente` en `espacio_importaciones`. Acá se las toma, se baja el archivo,
 * se marca cómo terminó y se lo borra. La conversión no vive acá: es
 * `shared/importacionesBot` con `importarLibro`, la misma del botón.
 *
 * La RLS hace la privacidad: cada uno ve y actualiza sólo las suyas
 * (`dueno = mi_mail()`), y en Storage lee y borra sólo su carpeta. Ver
 * 20261005110000_espacio_importaciones.sql.
 *
 * Se usa a través de `services/espacio` (que lo reexporta): así, quien mockea
 * el servicio de Mi espacio en un test no sale sin querer a Supabase por acá. */

export const BUCKET_IMPORTACIONES = "importaciones";

/* Cuánto puede quedar «procesando» una importación antes de que otra pestaña
   la pueda volver a tomar: la que la tenía se cerró en el medio. */
export const TOMA_VENCE_MS = 5 * 60 * 1000;

/* Las pendientes (y las que una pestaña dejó colgadas) y las de error que
   todavía no se le mostraron al dueño, de la más vieja a la más nueva. */
export async function importacionesParaAtender() {
  const { data, error } = await supabase
    .from("espacio_importaciones")
    .select("id, nombre, ruta, origen, estado, error, creada_en, tomada_en")
    .or("estado.in.(pendiente,procesando),and(estado.eq.error,avisada_en.is.null)")
    .order("creada_en", { ascending: true })
    .limit(20);
  if (error) throw error;
  return data || [];
}

/* La toma para esta pestaña SÓLO si sigue pendiente (o si la toma anterior
   venció). Si otra pestaña la tomó primero, no vuelve ninguna fila y ésta la
   deja: así dos pestañas abiertas no crean dos planillas. */
export async function tomarImportacion(id, ahora = new Date()) {
  const vencida = new Date(ahora.getTime() - TOMA_VENCE_MS).toISOString();
  const { data, error } = await supabase
    .from("espacio_importaciones")
    .update({ estado: "procesando", tomada_en: ahora.toISOString() })
    .eq("id", id)
    // La hora va entre comillas: adentro de un `or` de PostgREST, el «:» y el
    // «.» de un ISO son caracteres reservados.
    .or(`estado.eq.pendiente,and(estado.eq.procesando,tomada_en.lt."${vencida}")`)
    .select("id");
  if (error) throw error;
  return Boolean(data && data.length);
}

/* El archivo, como ArrayBuffer. */
export async function bajarArchivo(ruta) {
  const { data, error } = await supabase.storage.from(BUCKET_IMPORTACIONES).download(ruta);
  if (error) throw error;
  return data.arrayBuffer();
}

/* Se borra al terminar, salga bien o mal: ya no le sirve a nadie, y es una
   planilla de la empresa ocupando un bucket. Si falla no importa. */
export async function borrarArchivo(ruta) {
  if (!ruta) return;
  try {
    await supabase.storage.from(BUCKET_IMPORTACIONES).remove([ruta]);
  } catch { /* queda el archivo; la fila ya dice cómo terminó */ }
}

async function marcar(id, cambios) {
  const { error } = await supabase.from("espacio_importaciones").update(cambios).eq("id", id);
  if (error) throw error;
}

export const marcarHecha = (id, planillaId, ahora = new Date()) =>
  marcar(id, { estado: "hecha", planilla_id: planillaId, hecha_en: ahora.toISOString(), avisada_en: ahora.toISOString() });

export const marcarError = (id, texto, ahora = new Date()) =>
  marcar(id, { estado: "error", error: texto, avisada_en: ahora.toISOString() });

/* La devuelve a pendiente: falló algo nuestro (la red), y la próxima vez que
   se abra Mis planillas se reintenta. */
export const soltarImportacion = (id) => marcar(id, { estado: "pendiente", tomada_en: null });

export const marcarAvisada = (id, ahora = new Date()) => marcar(id, { avisada_en: ahora.toISOString() });
